import { useState, useEffect } from "react";
import { useData } from "@/contexts/DataContext";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { useLocation } from "wouter";
import {
  Image as ImageIcon,
  MessageSquare,
  ShieldCheck,
  Save,
  Loader2,
  ChevronLeft,
  FileText,
  Receipt,
  QrCode // Added QR Code icon
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export default function InvoiceSettings() {
  const [, setLocation] = useLocation();
  const {
    printerPrintLogo, setPrinterPrintLogo,
    invoiceFooter, setInvoiceFooter,
    invoiceTerms, setInvoiceTerms,
    invoiceLayout, setInvoiceLayout,
    printQrCode, setPrintQrCode,
    businessLogo, // Pulled directly from your settings object
    isUltimateOwnerMobile,
  } = useData();
  const { toast } = useToast();

  const [localFooter, setLocalFooter] = useState(invoiceFooter || "");
  const [localTerms, setLocalTerms] = useState(invoiceTerms || "");
  const [localLayout, setLocalLayout] = useState(invoiceLayout || 'standard');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setLocalFooter(invoiceFooter || "");
  }, [invoiceFooter]);

  useEffect(() => {
    setLocalTerms(invoiceTerms || "");
  }, [invoiceTerms]);

  useEffect(() => {
    if (invoiceLayout) setLocalLayout(invoiceLayout);
  }, [invoiceLayout]);

  const handleBack = () => {
    if (window.history.length > 1) {
      window.history.back();
    } else {
      setLocation("/");
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      // Saving text settings to Firestore via Context functions
      await setInvoiceFooter(localFooter);
      await setInvoiceTerms(localTerms);

      toast({
        title: "Settings Saved!",
        description: "Your invoice footer and terms have been updated.",
        className: "bg-green-50 border-green-200 text-green-900",
      });
    } catch (err) {
      toast({
        title: "Save Failed",
        description: "Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="flex flex-col min-h-screen bg-[#FFFBF2] dark:bg-[#1A1614]">
      {isUltimateOwnerMobile ? (
        <div className="px-4 pb-2" style={{ paddingTop: "calc(env(safe-area-inset-top) + 1.25rem)" }}>
          <div className="flex items-start gap-3">
            <button onClick={handleBack} className="rounded-full p-2 border border-slate-200 dark:border-[#454545] bg-white dark:bg-[#1F1B19]/60 mt-1 shrink-0">
              <ChevronLeft className="h-4 w-4 text-[#0F172A] dark:text-[#EEEAE3]" />
            </button>
            <div className="min-w-0">
              <h1 className="text-2xl font-black uppercase tracking-tighter leading-[0.95] text-[#0F172A] dark:text-[#EEEAE3]">
                INVOICE<br /><span className="text-[#8B1A1A]">SETTINGS</span>
              </h1>
              <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500 dark:text-[#8A8A92] mt-2">
                Layout · Footer · Terms
              </p>
            </div>
          </div>
        </div>
      ) : (
        <header className="px-4 py-4 flex items-center sticky top-0 z-30 bg-[#FFFBF2]/90 dark:bg-[#1A1614]/90 backdrop-blur-md">
          <button onClick={handleBack} className="w-10 h-10 bg-white dark:bg-slate-800 rounded-full flex items-center justify-center shadow-sm border border-slate-100 dark:border-slate-700 active:scale-95 transition-transform shrink-0">
            <ChevronLeft className="w-6 h-6 text-slate-800 dark:text-white" />
          </button>
          <h1 className="text-lg font-black text-slate-900 dark:text-white ml-4 tracking-tight">Invoice Settings</h1>
        </header>
      )}

      <div className="flex-1 px-4 py-4 pb-24 overflow-y-auto">
        <div className="space-y-4">

          {/* Invoice Layout Selection */}
          <div className="bg-white dark:bg-slate-900 rounded-3xl p-5 border border-slate-100 dark:border-slate-800 shadow-sm">
            <div className="flex items-center gap-3 mb-4">
              <FileText className="h-5 w-5 text-blue-500" />
              <Label className="font-bold text-sm">Invoice Layout</Label>
            </div>
            <Select value={localLayout} onValueChange={(v) => {
              const val = v as 'standard' | 'basic';
              setLocalLayout(val);
              if (setInvoiceLayout) {
                setInvoiceLayout(val); // Auto-save immediately
                toast({ title: "Layout updated", duration: 2000 });
              }
            }}>
              <SelectTrigger className="w-full bg-slate-50 dark:bg-slate-800/50 border-slate-100 dark:border-slate-800 rounded-xl h-12">
                <SelectValue placeholder="Select Layout" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="standard">
                  <div className="flex items-center gap-2">
                    <FileText className="h-4 w-4 text-slate-500" />
                    <span>Standard (Full Details)</span>
                  </div>
                </SelectItem>
                <SelectItem value="basic">
                  <div className="flex items-center gap-2">
                    <Receipt className="h-4 w-4 text-slate-500" />
                    <span>Basic (Compact)</span>
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
            <p className="text-[10px] text-slate-400 mt-2 ml-1">Select 'Basic' for the simplified receipt format you requested.</p>
          </div>

          {/* Logo Toggle */}
          <div className={`bg-white dark:bg-slate-900 rounded-3xl p-5 border border-slate-100 dark:border-slate-800 shadow-sm transition-all ${localLayout === 'basic' ? 'opacity-60' : ''}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-xl bg-slate-50 dark:bg-slate-800 flex items-center justify-center">
                  <ImageIcon className="h-5 w-5 text-slate-500" />
                </div>
                <div>
                  <p className="font-bold text-slate-900 dark:text-white text-sm">Print Logo</p>
                  <p className="text-xs text-slate-400">
                    {localLayout === 'basic'
                      ? "Only available for Standard layout"
                      : "Show your logo on thermal bills"}
                  </p>
                </div>
              </div>
              <Switch
                checked={printerPrintLogo}
                onCheckedChange={(v) => {
                  // Check if the user is trying to turn it on without a logo
                  if (v && !businessLogo) {
                    toast({
                      title: "Missing Logo",
                      description: "Please upload a business logo first.",
                      variant: "destructive",
                      duration: 3000
                    });
                    return; // Stop the switch from turning on
                  }

                  if (setPrinterPrintLogo) {
                    setPrinterPrintLogo(v);
                    toast({ title: v ? "Logo printing enabled" : "Logo printing disabled", duration: 2000 });
                  }
                }}
                disabled={localLayout === 'basic'}
              />
            </div>
          </div>

          {/* QR Code Toggle */}
          <div className={`bg-white dark:bg-slate-900 rounded-3xl p-5 border border-slate-100 dark:border-slate-800 shadow-sm transition-all ${localLayout === 'basic' ? 'opacity-60' : ''}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-xl bg-slate-50 dark:bg-slate-800 flex items-center justify-center">
                  <QrCode className="h-5 w-5 text-slate-500" />
                </div>
                <div>
                  <p className="font-bold text-slate-900 dark:text-white text-sm">Print QR Code</p>
                  <p className="text-xs text-slate-400">
                    {localLayout === 'basic'
                      ? "Only available for Standard layout"
                      : "Print scan-to-download link on receipt"}
                  </p>
                </div>
              </div>
              <Switch
                checked={printQrCode ?? false}
                onCheckedChange={(v) => {
                  if (setPrintQrCode) {
                    setPrintQrCode(v);
                    toast({ title: v ? "QR code printing enabled" : "QR code printing disabled", duration: 2000 });
                  }
                }}
                disabled={localLayout === 'basic'}
              />
            </div>
          </div>

          {/* Custom Footer Message */}
          <div className={`bg-white dark:bg-slate-900 rounded-3xl p-5 border border-slate-100 dark:border-slate-800 shadow-sm transition-all ${localLayout === 'basic' ? 'opacity-60' : ''}`}>
            <div className="flex items-center gap-3 mb-4">
              <MessageSquare className="h-5 w-5 text-emerald-500" />
              <Label className="font-bold text-sm">Footer Message</Label>
            </div>
            <textarea
              value={localFooter}
              onChange={(e) => setLocalFooter(e.target.value)}
              disabled={localLayout === 'basic'}
              placeholder="e.g. Thank you!&#10;Visit Again soon!"
              rows={2}
              className="w-full text-sm p-3 rounded-xl border border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/50 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 transition-all disabled:opacity-70 disabled:cursor-not-allowed"
            />
          </div>

          {/* Terms & Conditions */}
          <div className={`bg-white dark:bg-slate-900 rounded-3xl p-5 border border-slate-100 dark:border-slate-800 shadow-sm transition-all ${localLayout === 'basic' ? 'opacity-60' : ''}`}>
            <div className="flex items-center gap-3 mb-4">
              <ShieldCheck className="h-5 w-5 text-amber-500" />
              <Label className="font-bold text-sm">Terms & Conditions</Label>
            </div>
            <textarea
              value={localTerms}
              onChange={(e) => setLocalTerms(e.target.value)}
              disabled={localLayout === 'basic'}
              placeholder="e.g. No refund after 7 days"
              rows={3}
              className="w-full text-sm p-3 rounded-xl border border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/50 focus:outline-none focus:ring-2 focus:ring-amber-500/20 transition-all disabled:opacity-70 disabled:cursor-not-allowed"
            />
          </div>
        </div>

        <div className="mt-8">
          <Button
            onClick={handleSave}
            disabled={isSaving}
            className="w-full h-14 rounded-[2rem] bg-[#8B2121] hover:bg-[#6B1919] text-white font-bold text-lg shadow-lg shadow-red-900/20 active:scale-[0.98] transition-all flex items-center justify-center gap-2"
          >
            {isSaving ? <Loader2 className="h-5 w-5 animate-spin" /> : <Save className="h-5 w-5" />}
            {isSaving ? "Saving..." : "Save Changes"}
          </Button>
        </div>
      </div>
    </div>
  );
}